'use client'

import React, { useId, useRef, useState } from 'react'

import { modelliDellaMarca, profiloCodiceDellaMarca } from '../actions'
import { avvisoCodice, type ProfiloCodice, suggerimentoCodice } from '../formaCodice'
import type { MarcaOption, ModelloOption } from '../queries'
import {
  MODELLO_NON_IN_ELENCO,
  pulisciModelloLibero,
  TESTO_LIBERO_MAX,
  TESTO_LIBERO_MIN,
} from '../urls'

export type MarcaSelect = Pick<MarcaOption, 'label' | 'nTinte' | 'slug'>

/** `guidata`: brand + model (+ code), the default. `libera`: one text (`?testo=`). */
export type SchedaRicerca = 'guidata' | 'libera'

type Props = {
  marche: MarcaSelect[]
  marca?: string
  modello?: string
  /** With "Modello non in elenco": the model the customer typed. */
  modelloLibero?: string
  codice?: string
  /** Models of `marca`, rendered by the server so the select is filled without a round trip. */
  modelli?: ModelloOption[]
  /** Usual code shape of `marca`, rendered by the server like `modelli`. */
  profiloCodice?: ProfiloCodice | null
  /** Server-side validation of the URL ("Scegli il modello"). */
  errore?: string
  /** Help shown under the form once brand and model are chosen (where the plate is). */
  aiuto?: React.ReactNode
  /** The tab open on load: `libera` when the URL has `?testo=`. */
  scheda?: SchedaRicerca
  /** Free search text, from `?testo=`. */
  testo?: string
  /** Server-side validation of `?testo=` (too short or too long). */
  erroreTesto?: string
}

/**
 * The paint search, in two tabs. "Marca e modello": brand and model required, colour code
 * optional; the models are loaded when the brand changes. "Cerca nel sito": one text over
 * paint names and codes, models and shop products (`?testo=`). Both are a plain GET to /cercatinte, so the URL holds the
 * whole search. Both forms stay mounted, so switching tabs keeps what was typed.
 */
export const SearchWorkspace: React.FC<Props> = ({
  marche,
  marca: marcaIniziale = '',
  modello: modelloIniziale = '',
  modelloLibero: modelloLiberoIniziale = '',
  codice: codiceIniziale = '',
  modelli: modelliIniziali = [],
  profiloCodice: profiloIniziale = null,
  errore,
  aiuto,
  scheda: schedaIniziale = 'guidata',
  testo = '',
  erroreTesto,
}) => {
  const [scheda, setScheda] = useState(schedaIniziale)
  const [marca, setMarca] = useState(marcaIniziale)
  const [modello, setModello] = useState(modelloIniziale)
  const [modelli, setModelli] = useState(modelliIniziali)
  const [modelloLibero, setModelloLibero] = useState(modelloLiberoIniziale)
  const [caricamento, setCaricamento] = useState(false)
  const [codice, setCodice] = useState(codiceIniziale)
  const [profilo, setProfilo] = useState(profiloIniziale)
  // The warning waits until the customer leaves the field (or comes back with a code in the URL).
  const [codiceToccato, setCodiceToccato] = useState(Boolean(codiceIniziale))
  const idCodice = useId()
  // Only the answer for the latest brand counts, if the customer changes it quickly.
  const richiesta = useRef(0)

  const scegliMarca = async (slug: string) => {
    const n = ++richiesta.current
    setMarca(slug)
    setModello('')
    setModelli([])
    setProfilo(null)
    setCaricamento(Boolean(slug))
    if (!slug) return
    const [lista, profiloMarca] = await Promise.all([
      modelliDellaMarca(slug).catch(() => []),
      profiloCodiceDellaMarca(slug).catch(() => null),
    ])
    if (n !== richiesta.current) return
    setModelli(lista)
    setProfilo(profiloMarca)
    setCaricamento(false)
  }

  return (
    <div className="searchworkspace">
      <div aria-label="Come vuoi cercare" className="searchpaths" role="group">
        <button
          aria-controls="projectSearch"
          aria-pressed={scheda === 'guidata'}
          onClick={() => setScheda('guidata')}
          type="button"
        >
          <span>Marca e modello</span>
          <small>Le tinte della tua auto, con o senza codice</small>
        </button>
        <button
          aria-controls="ricercaLibera"
          aria-pressed={scheda === 'libera'}
          onClick={() => setScheda('libera')}
          type="button"
        >
          <span>Cerca nel sito</span>
          <small>Colori, codici, modelli e prodotti</small>
        </button>
      </div>

      <form
        action="/cercatinte"
        className="codeengine liberaform"
        hidden={scheda !== 'libera'}
        id="ricercaLibera"
        role="search"
      >
        {erroreTesto && (
          <p className="checkoutalert" role="alert">
            {erroreTesto}
          </p>
        )}
        <label htmlFor="testoLibero">Cerca colori, codici, modelli e prodotti</label>
        <div className="codeentry">
          <input
            autoComplete="off"
            // Opened from the header's "Cerca nel sito" link: ready to type.
            autoFocus={schedaIniziale === 'libera' && !testo}
            defaultValue={testo}
            id="testoLibero"
            maxLength={TESTO_LIBERO_MAX}
            minLength={TESTO_LIBERO_MIN}
            name="testo"
            placeholder="Es. rosso corsa, 296/A, Panda, trasparente"
            required
            type="search"
          />
          <button className="primary">Cerca →</button>
        </div>
        <p className="searchhint">
          Cerchiamo in tutto il sito: colori e codici di tutte le marche, modelli e prodotti del
          negozio. Per essere sicuri della tinta giusta, conferma poi marca e modello della tua
          auto.
        </p>
      </form>

      <form
        action="/cercatinte"
        className="codeengine tintaform"
        hidden={scheda !== 'guidata'}
        id="projectSearch"
      >
        {errore && (
          <p className="checkoutalert" role="alert">
            {errore}
          </p>
        )}
        <div className="tintafields">
          <label>
            Marca
            <select
              name="marca"
              onChange={(e) => scegliMarca(e.target.value)}
              required
              value={marca}
            >
              <option value="">Scegli la marca</option>
              {[...marche]
                .sort((a, b) => a.label.localeCompare(b.label, 'it'))
                .map((m) => (
                  <option key={m.slug} value={m.slug}>
                    {m.label}
                    {m.nTinte === 0 ? ' · solo su richiesta' : ''}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Modello
            <select
              aria-busy={caricamento}
              disabled={!marca || caricamento}
              name="modello"
              onChange={(e) => setModello(e.target.value)}
              required
              value={modello}
            >
              <option value="">
                {!marca
                  ? 'Prima scegli la marca'
                  : caricamento
                    ? 'Carico i modelli…'
                    : 'Scegli il modello'}
              </option>
              {modelli.map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.label}
                </option>
              ))}
              {marca && !caricamento && (
                <option value={MODELLO_NON_IN_ELENCO}>Modello non in elenco</option>
              )}
            </select>
          </label>
          {modello === MODELLO_NON_IN_ELENCO && (
            <label className="tintalibero">
              Il tuo modello <span>· scrivilo per intero, anche se ha il codice colore</span>
              <input
                autoComplete="off"
                maxLength={60}
                minLength={2}
                name="modelloLibero"
                onChange={(e) => setModelloLibero(e.target.value)}
                placeholder="Es. Punto Evo, Ypsilon 2011"
                required
                value={modelloLibero}
              />
            </label>
          )}
          <div className="tintacodice">
            <label>
              Codice colore <span>· facoltativo</span>
              <input
                aria-describedby={`${profilo ? `${idCodice}-aiuto ` : ''}${idCodice}-avviso`}
                autoComplete="off"
                name="codice"
                onBlur={() => setCodiceToccato(true)}
                onChange={(e) => setCodice(e.target.value)}
                placeholder={
                  profilo
                    ? `Es. ${profilo.tipiche
                        .map((t) => t.esempio)
                        .slice(0, 2)
                        .join(', ')}`
                    : 'Es. 647, 296/A'
                }
                value={codice}
              />
            </label>
            {profilo && (
              <small className="codiceesempio" id={`${idCodice}-aiuto`}>
                {suggerimentoCodice(profilo)}
              </small>
            )}
            <p aria-live="polite" className="codiceavviso" id={`${idCodice}-avviso`}>
              {(codiceToccato && avvisoCodice(codice, profilo)) || null}
            </p>
          </div>
        </div>
        <button
          className="primary"
          disabled={
            !marca ||
            !modello ||
            (modello === MODELLO_NON_IN_ELENCO && !pulisciModelloLibero(modelloLibero))
          }
        >
          Cerca →
        </button>
        <p className="searchhint">
          Marca e modello servono sempre, anche se hai il codice: lo stesso codice può indicare
          colori diversi, e la tinta va preparata per la tua auto. Con il codice trovi solo quella
          tinta; senza, ti mostriamo le tinte già viste sul tuo modello.
        </p>
      </form>

      {scheda === 'guidata' && aiuto}

      <ol aria-label="Come acquistare la tua tinta" className="painttimeline">
        <li>
          <span className="timelinepoint">01</span>
          <div>
            <b>Scegli l’auto</b>
            <small>Marca e modello, più il codice colore se lo conosci.</small>
          </div>
        </li>
        <li>
          <span className="timelinepoint">02</span>
          <div>
            <b>Conferma la tinta</b>
            <small>Controlla marca, modello e nome del colore.</small>
          </div>
        </li>
        <li>
          <span className="timelinepoint">03</span>
          <div>
            <b>Scegli il formato</b>
            <small>Dal pennarello allo spray, scegli ciò che ti serve.</small>
          </div>
        </li>
      </ol>
    </div>
  )
}
